import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box, Grid, TextField, Button, Typography, Alert, CircularProgress, Link, Divider,
  InputAdornment, IconButton,
} from '@mui/material';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { useAuth } from '../contexts/AuthContext';
import { brandColors, brandCss } from '../theme/appTheme';
import { authPrimaryButtonSx } from '../theme/formStyles';
import { AuthCoverLayout, authFont } from '../components/auth/AuthCoverLayout';
import { REGISTER_COVER } from '../components/auth/authCoverContent';
import { authTextFieldSx, authSectionTitleSx } from '../components/auth/authFieldStyles';

export const Register: React.FC = () => {
  const [formData, setFormData] = useState({
    companyName: '', companyEmail: '', companyMobile: '', gstNumber: '',
    addressLine1: '', country: 'IN', state: '', city: '', pincode: '',
    ownerName: '', ownerEmail: '', ownerMobile: '', password: '', confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { register, loading } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    try {
      const { confirmPassword, ...payload } = formData;
      await register(payload);
      setSuccess('Company registered successfully. Awaiting super admin approval.');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg || 'Registration failed');
    }
  };

  return (
    <AuthCoverLayout cover={REGISTER_COVER} formMaxWidth={480} scrollForm>
      <Typography
        sx={{
          ...authFont.heading,
          fontSize: { xs: '1.45rem', sm: '1.65rem' },
          color: brandColors.textDark,
          mb: 0.5,
        }}
      >
        Create account
      </Typography>
      <Typography
        sx={{
          ...authFont.body,
          fontSize: 14,
          color: brandColors.textMuted,
          mb: 2.5,
        }}
      >
        Register your transport company — takes a few minutes
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2.5 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2, borderRadius: 2.5 }}>{success}</Alert>}

      <Box component="form" onSubmit={handleSubmit}>
        <Typography sx={authSectionTitleSx}>Company</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Company Name"
              name="companyName"
              value={formData.companyName}
              onChange={handleChange}
              required
              fullWidth
              placeholder="e.g. Shani Express"
              sx={authTextFieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BusinessOutlinedIcon sx={{ fontSize: 20, color: brandColors.textMuted }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Company Email"
              type="email"
              name="companyEmail"
              value={formData.companyEmail}
              onChange={handleChange}
              required
              fullWidth
              placeholder="billing@company.com"
              sx={authTextFieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlinedIcon sx={{ fontSize: 20, color: brandColors.textMuted }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Mobile"
              name="companyMobile"
              value={formData.companyMobile}
              onChange={handleChange}
              required
              fullWidth
              placeholder="98XXXXXXXX"
              sx={authTextFieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneOutlinedIcon sx={{ fontSize: 20, color: brandColors.textMuted }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="GST Number"
              name="gstNumber"
              value={formData.gstNumber}
              onChange={handleChange}
              required
              fullWidth
              placeholder="22AAAAA0000A1Z5"
              sx={authTextFieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BadgeOutlinedIcon sx={{ fontSize: 20, color: brandColors.textMuted }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Address"
              name="addressLine1"
              value={formData.addressLine1}
              onChange={handleChange}
              required
              fullWidth
              placeholder="Street, area, landmark"
              sx={authTextFieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <HomeOutlinedIcon sx={{ fontSize: 20, color: brandColors.textMuted }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="State" name="state" value={formData.state} onChange={handleChange} required fullWidth placeholder="State" sx={authTextFieldSx} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="City" name="city" value={formData.city} onChange={handleChange} required fullWidth placeholder="City" sx={authTextFieldSx} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Pincode" name="pincode" value={formData.pincode} onChange={handleChange} required fullWidth placeholder="6 digits" sx={authTextFieldSx} />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2.75, borderColor: brandColors.border }} />
        <Typography sx={authSectionTitleSx}>Owner</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Owner Name"
              name="ownerName"
              value={formData.ownerName}
              onChange={handleChange}
              required
              fullWidth
              placeholder="Full name"
              sx={authTextFieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonOutlineOutlinedIcon sx={{ fontSize: 20, color: brandColors.textMuted }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Owner Email"
              name="ownerEmail"
              type="email"
              value={formData.ownerEmail}
              onChange={handleChange}
              required
              fullWidth
              placeholder="owner@company.com"
              sx={authTextFieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlinedIcon sx={{ fontSize: 20, color: brandColors.textMuted }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Owner Mobile"
              name="ownerMobile"
              value={formData.ownerMobile}
              onChange={handleChange}
              required
              fullWidth
              placeholder="98XXXXXXXX"
              sx={authTextFieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneOutlinedIcon sx={{ fontSize: 20, color: brandColors.textMuted }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2.75, borderColor: brandColors.border }} />
        <Typography sx={authSectionTitleSx}>Login password</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Password"
              type={showPassword ? 'text' : 'password'}
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              fullWidth
              placeholder="Min. 6 characters"
              helperText="Use a strong password you will remember"
              sx={authTextFieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon sx={{ fontSize: 20, color: brandColors.textMuted }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowPassword((v) => !v)} edge="end">
                      {showPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Confirm Password"
              type={showConfirm ? 'text' : 'password'}
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              fullWidth
              placeholder="Re-enter password"
              sx={authTextFieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon sx={{ fontSize: 20, color: brandColors.textMuted }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowConfirm((v) => !v)} edge="end">
                      {showConfirm ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>

        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={loading}
          fullWidth
          endIcon={!loading ? <ArrowForwardRoundedIcon /> : undefined}
          sx={{ ...authPrimaryButtonSx, mt: 3, borderRadius: '4px' }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : 'Register Company'}
        </Button>
      </Box>

      <Typography
        sx={{
          mt: 2.75,
          textAlign: 'center',
          color: brandColors.textMuted,
          fontFamily: authFont.family,
          fontSize: 13.5,
        }}
      >
        Already registered?{' '}
        <Link component={RouterLink} to="/login" underline="hover" sx={{ color: brandCss.primary, fontWeight: 700 }}>
          Sign in
        </Link>
      </Typography>
    </AuthCoverLayout>
  );
};
